import type {
  FastShopLowBalanceTriggerPayload,
  FastShopLowBalanceTriggerSource,
  FastShopRoundLowBalanceContext,
  FastShopSessionLowBalanceContext,
} from './fast-shop.schema';
import { useQueryClient } from '@tanstack/react-query';
import { createParsedEventHandler } from '@/hooks/socket/socket.helpers';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
import { useAppSelector } from '@/redux/hooks';
import { fastShopQueries } from './fast-shop.queries';
import {
  FastShopRoundLowBalanceContextSchema,
  FastShopSessionLowBalanceContextSchema,
} from './fast-shop.schema';

export const FAST_SHOP_LOW_BALANCE_SOCKET_EVENTS = {
  round: 'game.round.low-balance',
  session: 'game.session.low-balance',
} as const;

export const FAST_SHOP_LOW_BALANCE_TRIGGER_EVENT = 'fast-shop:low-balance-trigger';

export function dispatchFastShopLowBalanceTrigger(
  source: 'round',
  payload: FastShopRoundLowBalanceContext,
): void;
export function dispatchFastShopLowBalanceTrigger(
  source: 'session',
  payload: FastShopSessionLowBalanceContext,
): void;
export function dispatchFastShopLowBalanceTrigger(
  source: FastShopLowBalanceTriggerSource,
  payload: FastShopLowBalanceTriggerPayload,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(FAST_SHOP_LOW_BALANCE_TRIGGER_EVENT, {
    detail: {
      ...payload,
      source,
    },
  }));
}

export function useFastShopSocket() {
  const queryClient = useQueryClient();
  const userSession = useAppSelector(state => state.user.userSession);
  const isSessionActive = Boolean(userSession);

  const handleRoundLowBalance = createParsedEventHandler(
    FastShopRoundLowBalanceContextSchema,
    (payload) => {
      dispatchFastShopLowBalanceTrigger('round', payload);
    },
  );

  const handleSessionLowBalance = createParsedEventHandler(
    FastShopSessionLowBalanceContextSchema,
    (payload) => {
      dispatchFastShopLowBalanceTrigger('session', payload);
    },
  );

  useSocketEvent(
    FAST_SHOP_LOW_BALANCE_SOCKET_EVENTS.round,
    async (data) => {
      try {
        // Fast Offers are requested before the trigger is dispatched so the popup is never
        // opened with an empty carousel. Opening stays in `FastShopFlowController`, which
        // owns the duplicate-trigger guard and the fullscreen/hidden-tab pending path.
        const fastOffers = await queryClient.fetchQuery(fastShopQueries.meFastOffers());

        if (!fastOffers.length) {
          return;
        }

        handleRoundLowBalance(data);
      } catch {
        // A failed Fast Offers request must not interrupt gameplay: skip this trigger.
      }
    },
    isSessionActive,
  );

  useSocketEvent(
    FAST_SHOP_LOW_BALANCE_SOCKET_EVENTS.session,
    handleSessionLowBalance,
    isSessionActive,
  );
}
